import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import { useReceipt, useReceiptStore } from "hooks";
import {
  Receipt,
  ReceiptForm,
  ReceiptItem,
  ReceiptQuery,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import type { QueryState } from "components/ui-common/AgGridTable";

const defaultFormValues: ReceiptForm = {
  receipt_code: "",
  receipt_date: "",
  object_name: "",
  payer_name: "",
  address: "",
  description: "",
  attachments: "",
  original_voucher_no: "",
  export_invoice_id: undefined,
  items: [],
};

const normalizeItem = (item: Partial<ReceiptItem>): ReceiptItem => ({
  amount: Number(item.amount) || 0,
  partner_id:
    item.partner_id !== undefined && item.partner_id !== null
      ? Number(item.partner_id)
      : undefined,
  reason: item.reason ? String(item.reason).trim() : undefined,
  vat_rate:
    item.vat_rate !== undefined && item.vat_rate !== null
      ? Number(item.vat_rate)
      : undefined,
  pit_rate:
    item.pit_rate !== undefined && item.pit_rate !== null
      ? Number(item.pit_rate)
      : undefined,
});

const parseItems = (value: unknown): ReceiptItem[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeItem(item || {}))
      .filter((item) => !Number.isNaN(item.amount));
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => normalizeItem(item || {}))
          .filter((item) => !Number.isNaN(item.amount));
      }
    } catch {
      // fall back to empty when parsing fails
    }
  }

  return [];
};

const Receipts: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<Receipt | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<ReceiptQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<Receipt | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useReceiptStore();

  const { fetchList, createReceipt, updateReceipt, deleteReceipt } =
    useReceipt();

  const {
    data: receiptData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  useEffect(() => {
    if (receiptData?.data) {
      setRows(receiptData.data as Receipt[]);
    }
  }, [receiptData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createReceipt.isPending ||
        updateReceipt.isPending ||
        deleteReceipt.isPending,
      ),
    );
  }, [
    isFetching,
    createReceipt.isPending,
    updateReceipt.isPending,
    deleteReceipt.isPending,
    setLoading,
  ]);

  const columns = useMemo<ColDef<Receipt>[]>(() => {
    return [
      {
        headerName: "Mã phiếu thu",
        field: "receipt_code",
        minWidth: 160,
        editable: true,
      },
      {
        headerName: "Ngày phiếu thu",
        field: "receipt_date",
        minWidth: 150,
        editable: true,
      },
      {
        headerName: "Đơn vị/Đối tượng",
        field: "object_name",
        minWidth: 200,
        editable: true,
      },
      {
        headerName: "Người nộp",
        field: "payer_name",
        minWidth: 180,
        editable: true,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        editable: true,
      },
      {
        headerName: "Nội dung",
        field: "description",
        flex: 1,
        minWidth: 220,
        editable: true,
      },
      {
        headerName: "Đính kèm",
        field: "attachments",
        minWidth: 180,
        editable: true,
        valueFormatter: (params) => params.value || "",
      },
      {
        headerName: "Số CT gốc",
        field: "original_voucher_no",
        minWidth: 140,
        editable: true,
      },
      {
        headerName: "Hóa đơn xuất (ID)",
        field: "export_invoice_id",
        minWidth: 150,
        editable: true,
        valueParser: (value) => {
          const parsed = Number(value);
          return Number.isNaN(parsed) ? undefined : parsed;
        },
      },
      {
        headerName: "Dòng thu",
        field: "items",
        flex: 1,
        minWidth: 260,
        editable: true,
        valueFormatter: (params) => {
          if (Array.isArray(params.value)) {
            if (!params.value.length) return "";
            return params.value
              .map((item) => {
                const amount = item.amount ?? 0;
                const reason = item.reason ? ` - ${item.reason}` : "";
                return `${amount}${reason}`;
              })
              .join("; ");
          }
          if (typeof params.value === "string") return params.value;
          return "";
        },
        valueSetter: (params) => {
          const parsed = parseItems(params.newValue);
          (params.data as Receipt).items = parsed;
          return true;
        },
      },
    ];
  }, []);

  const normalizePayload = useCallback(
    (row: Partial<Receipt>): ReceiptForm => ({
      receipt_code: String(row.receipt_code ?? "").trim(),
      receipt_date: String(row.receipt_date ?? "").trim(),
      object_name: row.object_name ? String(row.object_name).trim() : "",
      payer_name: row.payer_name ? String(row.payer_name).trim() : "",
      address: row.address ? String(row.address).trim() : "",
      description: row.description ? String(row.description).trim() : "",
      attachments: row.attachments ? String(row.attachments).trim() : "",
      original_voucher_no: row.original_voucher_no
        ? String(row.original_voucher_no).trim()
        : "",
      export_invoice_id:
        row.export_invoice_id !== undefined && row.export_invoice_id !== null
          ? Number(row.export_invoice_id) || undefined
          : undefined,
      items: parseItems(row.items),
    }),
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      ...defaultFormValues,
      receipt_date: new Date().toISOString().slice(0, 10),
    } as Receipt;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<Receipt>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.receipt_code || !payload.receipt_date) {
        SnackbarUtils.error("Mã phiếu thu và Ngày phiếu thu là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createReceipt.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Receipt);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm phiếu thu không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được phiếu thu cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateReceipt.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as Receipt);
            } else {
              upsertRow({ ...(row as Receipt), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật phiếu thu không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [createReceipt, normalizePayload, refetch, resetInlineAddRow, updateReceipt, upsertRow],
  );

  const handleSaveRow = useCallback(
    (row: Receipt, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
    },
    [persistRow],
  );

  const handleDeleteRow = useCallback((row: Receipt) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReceipt.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu thu không thành công");
      },
    });
  }, [confirmRow, deleteReceipt, editingId, removeRow, setEditingId]);

  const toggleInlineAddRow = useCallback(() => {
    setShowInlineAddRow((prev) => {
      const next = !prev ? 'add' : '';
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      editable: true,
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Receipts</h1>
        <p className="text-sm text-gray-600">
          Quản lý phiếu thu bằng grid: tạo mới, chỉnh sửa nhanh và xoá trực tiếp.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu thu
            </h2>
            <p className="text-xs text-gray-500">
              Thao tác CRUD trực tiếp trên lưới, không cần form.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm nhanh trên bảng"}
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={560}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={receiptData?.totalPage}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onEdit={() => { }}
          getRowId={(params) => String(params.data.id)}
          stopEditingWhenCellsLoseFocus
          suppressClickEdit={false}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu thu, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu thu?"
        description={`Bạn chắc chắn muốn xóa phiếu thu "${confirmRow?.receipt_code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReceipt.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default Receipts;
